import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import api from './api';
import { disconnectChatSocket } from './chatSocket';

const STORAGE_KEY = 'featureConfig';
const STORAGE_UPDATED_AT_KEY = 'featureConfigUpdatedAt';
const POLL_INTERVAL_MS = 60_000;
const BROADCAST_CHANNEL_NAME = 'feature-config';

const DEFAULT_FEATURES = {
  payments: false,
  turnstile: false,
  email: false,
  messaging: true,
  psychologist: true,
  registration: true,
  analytics: true,
  recommendations: true,
  pwa: true,
  yandexMetrika: false,
  promoCodes: true,
  trial: true,
};

const DEFAULT_CONFIG = {
  features: DEFAULT_FEATURES,
  turnstileSiteKey: '',
  metrikaCounterId: '',
  updatedAt: null,
};

const readStoredConfig = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      features: { ...DEFAULT_FEATURES, ...(parsed.features || {}) },
      turnstileSiteKey: parsed.turnstileSiteKey || '',
      metrikaCounterId: parsed.metrikaCounterId || '',
      updatedAt: parsed.updatedAt || sessionStorage.getItem(STORAGE_UPDATED_AT_KEY) || null,
    };
  } catch {
    return null;
  }
};

const persistConfig = (config) => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
    features: config.features,
    turnstileSiteKey: config.turnstileSiteKey,
    metrikaCounterId: config.metrikaCounterId,
    updatedAt: config.updatedAt,
  }));
  if (config.updatedAt) {
    sessionStorage.setItem(STORAGE_UPDATED_AT_KEY, config.updatedAt);
  }
};

const isRemoteNewer = (remoteUpdatedAt, localUpdatedAt) => {
  if (!remoteUpdatedAt) return false;
  if (!localUpdatedAt) return true;
  return new Date(remoteUpdatedAt).getTime() > new Date(localUpdatedAt).getTime();
};

const FeatureConfigContext = createContext({
  ...DEFAULT_CONFIG,
  loading: true,
  refreshConfig: async () => {},
});

export function FeatureConfigProvider({ children }) {
  const initialStoredRef = useRef(readStoredConfig());
  const stored = initialStoredRef.current;
  const [features, setFeatures] = useState(stored?.features || DEFAULT_FEATURES);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(stored?.turnstileSiteKey || '');
  const [metrikaCounterId, setMetrikaCounterId] = useState(stored?.metrikaCounterId || '');
  const [updatedAt, setUpdatedAt] = useState(stored?.updatedAt || null);
  const [loading, setLoading] = useState(!stored);
  const updatedAtRef = useRef(updatedAt);
  const channelRef = useRef(null);

  useEffect(() => {
    updatedAtRef.current = updatedAt;
  }, [updatedAt]);

  const applyConfig = useCallback((config) => {
    const next = {
      features: { ...DEFAULT_FEATURES, ...(config.features || {}) },
      turnstileSiteKey: config.turnstileSiteKey || '',
      metrikaCounterId: config.metrikaCounterId || '',
      updatedAt: config.updatedAt || null,
    };
    setFeatures(next.features);
    setTurnstileSiteKey(next.turnstileSiteKey);
    setMetrikaCounterId(next.metrikaCounterId);
    setUpdatedAt(next.updatedAt);
    persistConfig(next);
    return next;
  }, []);

  const refreshConfig = useCallback(async () => {
    const data = await api.getPublicConfig();
    return applyConfig(data);
  }, [applyConfig]);

  const checkVersion = useCallback(async () => {
    try {
      const version = await api.getPublicConfigVersion();
      if (isRemoteNewer(version.updatedAt, updatedAtRef.current)) {
        await refreshConfig();
      }
    } catch (err) {
      console.error('Failed to check feature config version:', err);
    }
  }, [refreshConfig]);

  useEffect(() => {
    let cancelled = false;

    const loadConfig = async () => {
      try {
        if (!stored) {
          setLoading(true);
        }
        const data = await api.getPublicConfig();
        if (!cancelled) {
          applyConfig(data);
        }
      } catch (err) {
        console.error('Failed to load public config:', err);
        if (!cancelled && !stored) {
          applyConfig(DEFAULT_CONFIG);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, [applyConfig]);

  useEffect(() => {
    const intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    let channel = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channelRef.current = channel;
      channel.onmessage = (event) => {
        if (isRemoteNewer(event.data?.updatedAt, updatedAtRef.current)) {
          refreshConfig().catch((err) => {
            console.error('Failed to refresh feature config:', err);
          });
        }
      };
    }

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
      channel?.close();
      channelRef.current = null;
    };
  }, [checkVersion, refreshConfig]);

  useEffect(() => {
    if (!features.messaging) {
      disconnectChatSocket();
    }
  }, [features.messaging]);

  const refreshAndBroadcast = useCallback(async () => {
    const next = await refreshConfig();
    channelRef.current?.postMessage({ updatedAt: next.updatedAt });
    return next;
  }, [refreshConfig]);

  const value = useMemo(
    () => ({
      features,
      turnstileSiteKey,
      metrikaCounterId,
      updatedAt,
      loading,
      refreshConfig: refreshAndBroadcast,
    }),
    [features, turnstileSiteKey, metrikaCounterId, updatedAt, loading, refreshAndBroadcast]
  );

  return (
    <FeatureConfigContext.Provider value={value}>
      {children}
    </FeatureConfigContext.Provider>
  );
}

export function useFeatureConfig() {
  return useContext(FeatureConfigContext);
}

export function useFeature(name) {
  const { features, loading } = useFeatureConfig();
  return {
    enabled: Boolean(features?.[name]),
    loading,
  };
}
